/**
 * RC Delivery Status Badge — renders a colored pill indicating the live
 * lifecycle state of an RC SMS or call activity row.
 *
 * Expected row shape (fields coming from /api/integrations/rc/activity):
 *   {
 *     type: "rc_sms_send" | "rc_call_make" | ...,
 *     delivery_status: "queued" | "sent" | "delivered" | "failed"
 *                    | "ringing" | "answered" | "completed" | "missed" | "voicemail"
 *                    | "no_answer" | "busy",
 *     error_code?: string,
 *     updated_at?: string,
 *   }
 */
import React from "react";

const STATUS_STYLES = {
  // SMS lifecycle
  queued:    { bg: "#EEF2F7", fg: "#475569", label: "Queued" },
  sent:      { bg: "#DBEAFE", fg: "#1D4ED8", label: "Sent" },
  delivered: { bg: "#D1FAE5", fg: "#047857", label: "Delivered" },
  // Call lifecycle
  ringing:   { bg: "#FEF3C7", fg: "#B45309", label: "Ringing", pulse: true },
  answered:  { bg: "#DBEAFE", fg: "#1D4ED8", label: "Answered" },
  completed: { bg: "#D1FAE5", fg: "#047857", label: "Completed" },
  voicemail: { bg: "#E0E7FF", fg: "#4338CA", label: "Voicemail" },
  // Terminal failures
  failed:    { bg: "#FEE2E2", fg: "#B91C1C", label: "Failed" },
  missed:    { bg: "#FEE2E2", fg: "#B91C1C", label: "Missed" },
  no_answer: { bg: "#FEE2E2", fg: "#B91C1C", label: "No Answer" },
  busy:      { bg: "#FEE2E2", fg: "#B91C1C", label: "Busy" },
  // Legacy / synchronous result fallbacks
  initiated: { bg: "#FEF3C7", fg: "#B45309", label: "Initiated", pulse: true },
  success:   { bg: "#D1FAE5", fg: "#047857", label: "Success" },
};

export default function DeliveryStatusBadge({ status, errorCode, size = "sm" }) {
  const key = (status || "").toString().toLowerCase();
  const cfg = STATUS_STYLES[key] || { bg: "#F1F5F9", fg: "#64748B", label: status || "—" };
  const sizing = size === "sm"
    ? { padding: "2px 8px", fontSize: 10, borderRadius: 999 }
    : { padding: "4px 10px", fontSize: 11, borderRadius: 999 };
  const tip = errorCode ? `Error: ${errorCode}` : cfg.label;
  return (
    <span
      title={tip}
      data-testid={`rc-status-${key || "unknown"}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: cfg.bg,
        color: cfg.fg,
        fontWeight: 600,
        letterSpacing: 0.2,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        ...sizing,
      }}
    >
      {cfg.pulse && (
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: cfg.fg,
            animation: "rcPulse 1.2s ease-in-out infinite",
          }}
        />
      )}
      {cfg.label}
      {errorCode && (
        <span style={{ opacity: 0.75, fontWeight: 500, fontSize: "0.9em" }}>· {errorCode}</span>
      )}
    </span>
  );
}

// Inject keyframes once (SSR-safe guard)
if (typeof document !== "undefined" && !document.getElementById("rc-pulse-keyframes")) {
  const style = document.createElement("style");
  style.id = "rc-pulse-keyframes";
  style.textContent = `@keyframes rcPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }`;
  document.head.appendChild(style);
}
